// @flow

import React, { Fragment, Component } from 'react';
import styled from 'styled-components';
import { Spinner, Alert, Intent, Checkbox } from '@blueprintjs/core';
import RolePicker from '../pickers/user-role-picker';
import NoAreasPanel from '../no-areas-panel';
import {
  TableHeadings,
  TableHeadingColumn,
  RolesHeadingColumn,
  FirstCellHeadingColumn,
  TableRowItem,
  TableRow,
  Table,
  StyledButton,
  AlertSpinner
} from './table';

import * as UserListModel from '../../models/user-list';
import * as UserModel from '../../models/user';
import * as RoleModel from '../../models/role';

import { inviteUser } from '../../services/auth-api';
import { errorToast } from '../../utils/toaster';
import { USER_TYPE } from '../../constants/user-types';
import PreferencesCell from '../user-profile/preferences-cell';
import type { UserProfilePermissionsT } from '../user-profile/use-user-profile-permissions';

const StyledTableRowItem = styled(TableRowItem)`
  color: rgba(0, 0, 0, 0.6);

  ${StyledButton}:first-child {
    margin-right: 1rem;
  }
`;

type Props = {
  users?: UserListModel.t,
  loading?: boolean,
  currentUser: UserModel.t,
  deleteUser(userId: number, userType: UserListModel.userListTypeEnumT): mixed,
  addRole(user: UserListModel.userT, role: RoleModel.catalogueRoleT): mixed,
  removeRole(user: UserListModel.userT, roleId: number): mixed,
  rolesCatalogue: RoleModel.RolesCatalogueT,
  rolesIncludeLoaded: { [UserListModel.userListTypeEnumT]: boolean },
  selectable?: boolean,
  selectedIds?: Set<number>,
  onToggleRow?: (id: number) => mixed,
  onToggleAll?: (visibleIds: number[]) => mixed,
  filterPredicate?: (user: UserListModel.userT) => boolean,
  permissions?: UserProfilePermissionsT,
};

type State = {
  userToDelete: ?UserListModel.userT,
  deleteUserInProgress: boolean,
  roleUpdatingForUser: ?number
};

// TODO: this component is almost identical to the active-users table - consider merging them to reduce code
class InvitedUsersTable extends Component<Props, State> {
  state = {
    userToDelete: null,
    deleteUserInProgress: false,
    roleUpdatingForUser: null
  };

  headerCheckboxRef: { current: ?HTMLInputElement } = { current: null };

  setUserToDelete(userToDelete: ?UserListModel.userT) {
    this.setState({ userToDelete });
  }

  async handleDeleteConfirm(userId: number) {
    const { deleteUser } = this.props;
    const { userToDelete } = this.state;

    if (userToDelete) {
      this.setState({ deleteUserInProgress: true });
      await deleteUser(userToDelete.id, USER_TYPE.INVITED);
      this.setState({ userToDelete: null, deleteUserInProgress: false });
    }
  }

  async toggleRole(user: UserListModel.userT, role: RoleModel.catalogueRoleT) {
    this.setState({ roleUpdatingForUser: user.id });
    if (user && user.roles && !!user.roles.find(r => r.id === role.id)) {
      await this.props.removeRole(user, role.id);
    } else {
      await this.props.addRole(user, role);
    }
    this.setState({ roleUpdatingForUser: null });
  }

  async resendEmail(user: UserListModel.userT) {
    const { currentUser } = this.props;

    if (user.roles) {
      const roles = user.roles.map(role => role.id);

      const { organisationId: orgId, organisationName: orgName } = currentUser;

      try {
        await inviteUser(orgName, user.email, roles, orgId);

        errorToast({
          message: 'Email sent',
          intent: Intent.SUCCESS
        });
      } catch (e) {
        errorToast({
          message: 'Failed to send the email.',
          intent: Intent.DANGER
        });
      }
    }
  }

  componentDidUpdate() {
    const { selectable, selectedIds } = this.props;
    if (!selectable) return;
    const filtered = this.getFilteredUsers();
    const visibleIds = filtered.map((u) => u.id);
    const allSelected =
      visibleIds.length > 0 &&
      !!selectedIds &&
      visibleIds.every((id) => selectedIds.has(id));
    const someSelected =
      !!selectedIds &&
      visibleIds.some((id) => selectedIds.has(id)) &&
      !allSelected;
    if (this.headerCheckboxRef.current) {
      this.headerCheckboxRef.current.indeterminate = someSelected;
    }
  }

  getFilteredUsers(): UserListModel.userT[] {
    const { users, filterPredicate } = this.props;
    if (!users) return [];
    if (!filterPredicate) return users;
    return users.filter(filterPredicate);
  }

  render() {
    const {
      loading,
      rolesCatalogue,
      rolesIncludeLoaded,
      selectable,
      selectedIds,
      onToggleRow,
      onToggleAll,
      permissions,
    } = this.props;
    const {
      userToDelete,
      deleteUserInProgress,
      roleUpdatingForUser
    } = this.state;

    if (loading) {
      return <NoAreasPanel message={<Spinner size={22} />} />;
    }

    const filtered = this.getFilteredUsers();

    if (!filtered || filtered.length === 0) {
      return <NoAreasPanel message="No users" />;
    }

    const visibleIds = filtered.map((u) => u.id);
    const allSelected =
      selectable &&
      selectedIds &&
      visibleIds.length > 0 &&
      visibleIds.every((id) => selectedIds.has(id));
    const someSelected =
      selectable &&
      selectedIds &&
      visibleIds.some((id) => selectedIds.has(id)) &&
      !allSelected;

    return (
      <Fragment>
        <Table>
          <thead>
            <TableHeadings>
              {selectable && (
                <TableHeadingColumn style={{ width: 40 }}>
                  <Checkbox
                    checked={!!allSelected}
                    onChange={() =>
                      onToggleAll && onToggleAll(visibleIds)
                    }
                    aria-label="Select all visible"
                    aria-checked={
                      someSelected ? 'mixed' : allSelected ? 'true' : 'false'
                    }
                    inputRef={(el) => {
                      this.headerCheckboxRef.current = el;
                    }}
                    style={{ margin: 0 }}
                  />
                </TableHeadingColumn>
              )}
              <FirstCellHeadingColumn>Invited Users</FirstCellHeadingColumn>
              <RolesHeadingColumn>Roles</RolesHeadingColumn>
              <TableHeadingColumn>Preferences</TableHeadingColumn>
              <TableHeadingColumn>Actions</TableHeadingColumn>
            </TableHeadings>
          </thead>
          <tbody>
            {filtered.map(user => {
              return (
                <TableRow key={`invited-users-key-${user.id}`}>
                  {selectable && (
                    <StyledTableRowItem>
                      <Checkbox
                        checked={!!selectedIds && selectedIds.has(user.id)}
                        onChange={() => onToggleRow && onToggleRow(user.id)}
                        aria-label="Select user"
                        style={{ margin: 0 }}
                      />
                    </StyledTableRowItem>
                  )}
                  <StyledTableRowItem>{user.email}</StyledTableRowItem>
                  <StyledTableRowItem>
                    <RolePicker
                      rolesCatalogue={rolesCatalogue}
                      rolesDataAvailable={rolesIncludeLoaded[user.userListType]}
                      selectedRoles={user.roles}
                      onToggle={role => this.toggleRole(user, role)}
                      updating={roleUpdatingForUser === user.id}
                    />
                  </StyledTableRowItem>
                  <StyledTableRowItem>
                    {permissions && (
                      <PreferencesCell
                        userId={user.id}
                        permissions={permissions}
                      />
                    )}
                  </StyledTableRowItem>

                  <StyledTableRowItem>
                    <StyledButton
                      text="Remove user"
                      minimal
                      outlined
                      onClick={() => this.setUserToDelete(user)}
                    />
                    <StyledButton
                      text="Resend invitation email"
                      minimal
                      outlined
                      onClick={() => this.resendEmail(user)}
                    />
                  </StyledTableRowItem>
                </TableRow>
              );
            })}
          </tbody>
        </Table>

        <Alert
          cancelButtonText="Cancel"
          confirmButtonText="Delete user"
          icon={deleteUserInProgress ? <AlertSpinner size="40" /> : 'trash'}
          intent={Intent.DANGER}
          isOpen={!!userToDelete}
          onCancel={() => {
            this.setUserToDelete(null);
          }}
          onConfirm={this.handleDeleteConfirm.bind(this)}
        >
          <p>
            Are you sure you want to delete {userToDelete && userToDelete.email}
            ?
          </p>
        </Alert>
      </Fragment>
    );
  }
}

export default InvitedUsersTable;
